if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        // Core elements (viewer + live region remain inside the component)
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]')
        };

        // Collect ALL thumbnail groups: internal + external via data-thumbnails (comma-separated allowed)
        this.thumbnailGroups = [];
        const internal = this.querySelector('[id^="GalleryThumbnails"]');
        if (internal) this.thumbnailGroups.push(internal);

        const extSel = (this.dataset.thumbnails || '').trim();
        if (extSel) {
          extSel
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
            .forEach((sel) => {
              document.querySelectorAll(sel).forEach((el) => {
                if (el && !this.thumbnailGroups.includes(el)) this.thumbnailGroups.push(el);
              });
            });
        }

        this.mql = window.matchMedia('(min-width: 750px)');

        // If there are no thumbnail groups, we don't need to wire anything
        if (this.thumbnailGroups.length === 0) return;

        // Viewer -> reflect current slide on ALL thumbnail groups
        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 500)
        );

        // Click in ANY thumbnail group -> set active media
        this.thumbnailGroups.forEach((group) => {
          group.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            const btn = mediaToSwitch.querySelector('button');
            if (!btn) return;
            btn.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        });

        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement?.dataset?.mediaId;
        if (!mediaId) return;

        // Update aria-current and scroll visibility in ALL groups
        this.thumbnailGroups.forEach((group) => {
          const thumb = group.querySelector(`[data-target="${mediaId}"]`);
          if (thumb) this.setActiveThumbnail(thumb, group);
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) return;

        // Switch active slide
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((el) => el.classList.remove('is-active'));
        activeMedia.classList.add('is-active');

        if (prepend) {
          // Reorder active media to the beginning
          if (activeMedia.parentElement.firstChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }

          // Reorder the active thumbnail in EVERY group
          this.thumbnailGroups.forEach((group) => {
            const activeThumb = group.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumb && activeThumb.parentElement.firstChild !== activeThumb) {
              activeThumb.parentElement.prepend(activeThumb);
            }
          });

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (!this.mql.matches || this.thumbnailGroups.length > 0) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const rect = activeMedia.getBoundingClientRect();
          // Don't scroll page if already in view
          if (rect.top > -0.5) return;
          const top = rect.top + window.scrollY;
          window.scrollTo({ top, behavior: 'smooth' });
        });

        this.playActiveMedia(activeMedia);

        // Update ALL groups' active states; announce once (from first group that has the thumb)
        let announced = false;
        this.thumbnailGroups.forEach((group) => {
          const activeThumb = group.querySelector(`[data-target="${mediaId}"]`);
          if (!activeThumb) return;

          this.setActiveThumbnail(activeThumb, group);

          if (!announced && activeThumb.dataset.mediaPosition) {
            this.announceLiveRegion(activeMedia, activeThumb.dataset.mediaPosition);
            announced = true;
          }
        });
      }

      // Updated to accept the specific group we're updating
      setActiveThumbnail(thumbnail, group) {
        if (!group || !thumbnail) return;

        // aria-current management
        group.querySelectorAll('button').forEach((el) => el.removeAttribute('aria-current'));
        const btn = thumbnail.querySelector('button');
        if (btn) btn.setAttribute('aria-current', 'true');

        // Keep the active thumb scrolled into view if the group's slider API exists
        // Dawn exposes group.slider and group.isSlideVisible on <slider-component>
        const hasVisibilityAPI = typeof group.isSlideVisible === 'function';
        if (hasVisibilityAPI && group.isSlideVisible(thumbnail, 10)) return;

        if (group.slider) {
          group.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          if (!this.elements.liveRegion) return;
          this.elements.liveRegion.setAttribute('aria-hidden', 'false');
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', 'true');
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
